'use client';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useReducer } from 'react';

// Components
import CartModal from '@/components/cart/modal';
import Typography from '@/components/typography/typography';
import { BlockElement } from '@/components/ui/button';
import SearchToggle from './searchToggle';

// Data
import { menuData } from '@/data/menu';
import { Category, MenuItem, SubCategory } from '@/types/menu';

// Types & State Management
interface MenuState {
  searchOpen: boolean;
  isMenuOpen: boolean;
  activeCategory: string;
  activeSubCategory: string;
  activeNestedSubCategory: string;
  showProductSort: boolean;
  activeSort: string;
}

type MenuAction =
  | { type: 'TOGGLE_SEARCH' }
  | { type: 'SET_SEARCH'; payload: boolean }
  | { type: 'TOGGLE_MENU' }
  | { type: 'CLOSE_MENU' }
  | { type: 'SET_CATEGORY'; payload: string }
  | { type: 'SET_SUBCATEGORY'; payload: string }
  | { type: 'SET_NESTED_SUBCATEGORY'; payload: string }
  | { type: 'SET_SORT_TYPE'; payload: string };

const initialState: MenuState = {
  searchOpen: false,
  isMenuOpen: false,
  activeCategory: 'portablePowerStations',
  activeSubCategory: 'byProduct',
  activeNestedSubCategory: '',
  showProductSort: true,
  activeSort: 'products'
};

function menuReducer(state: MenuState, action: MenuAction): MenuState {
  switch (action.type) {
    case 'TOGGLE_SEARCH':
      return { ...state, searchOpen: !state.searchOpen };
    case 'SET_SEARCH':
      return { ...state, searchOpen: action.payload };
    case 'TOGGLE_MENU':
      return { ...state, isMenuOpen: !state.isMenuOpen };
    case 'CLOSE_MENU':
      return { ...state, isMenuOpen: false };
    case 'SET_CATEGORY': {
      const category = action.payload;
      const categoryData = menuData[category];

      // Check if this category has sorting options
      const hasSorting = [
        'portablePowerStations',
        'evCharging',
        'commercialAndHomeStorage'
      ].includes(category);

      // Determine first subcategory and nested subcategory
      let firstSubcategoryKey = '';
      let firstNestedSubcategoryKey = '';

      if (hasSorting && categoryData?.subcategories?.byProduct) {
        firstSubcategoryKey = 'byProduct';

        if (categoryData.subcategories.byProduct.subcategories) {
          firstNestedSubcategoryKey =
            Object.keys(categoryData.subcategories.byProduct.subcategories)[0] || '';
        }
      } else if (category === 'evCharging' && categoryData?.subcategories) {
        // Special handling for EV Charging
        firstSubcategoryKey = 'evcharger';

        if (categoryData.subcategories.evcharger?.subcategories) {
          firstNestedSubcategoryKey =
            Object.keys(categoryData.subcategories.evcharger.subcategories)[0] || '';
        }
      } else if (categoryData?.subcategories) {
        firstSubcategoryKey = Object.keys(categoryData.subcategories)[0] || '';
      }

      return {
        ...state,
        activeCategory: category,
        activeSubCategory: firstSubcategoryKey,
        activeNestedSubCategory: firstNestedSubcategoryKey,
        isMenuOpen: true,
        showProductSort: hasSorting,
        activeSort: hasSorting ? 'products' : ''
      };
    }
    case 'SET_SUBCATEGORY': {
      const subcategory = action.payload;

      // Determine sort type based on subcategory
      let newActiveSort = state.activeSort;

      if (subcategory === 'byScenarios') {
        newActiveSort = 'scenarios';
      } else if (subcategory === 'byProduct') {
        newActiveSort = 'products';
      }

      return {
        ...state,
        activeSubCategory: subcategory,
        activeNestedSubCategory: '', // Reset nested subcategory
        activeSort: newActiveSort,
        showProductSort: newActiveSort === 'products'
      };
    }
    case 'SET_NESTED_SUBCATEGORY':
      return {
        ...state,
        activeNestedSubCategory: action.payload
      };
    case 'SET_SORT_TYPE': {
      const sortType = action.payload;
      let newSubCategory = state.activeSubCategory;
      let newNestedSubCategory = '';

      // Handle subcategory changes based on sort type
      if (sortType === 'scenarios' && menuData[state.activeCategory]?.subcategories?.byScenarios) {
        newSubCategory = 'byScenarios';
      } else if (
        sortType === 'products' &&
        menuData[state.activeCategory]?.subcategories?.byProduct
      ) {
        newSubCategory = 'byProduct';

        // Fix the type error by ensuring all objects in the chain are defined
        const activeCategory = menuData[state.activeCategory];
        const byProductSubcategories = activeCategory?.subcategories?.byProduct?.subcategories;

        if (byProductSubcategories && Object.keys(byProductSubcategories).length > 0) {
          newNestedSubCategory = Object.keys(byProductSubcategories)[0] || '';
        }
      }

      return {
        ...state,
        activeSort: sortType,
        activeSubCategory: newSubCategory,
        activeNestedSubCategory: newNestedSubCategory,
        showProductSort: sortType === 'products'
      };
    }
    default:
      return state;
  }
}

// Reusable components
const ProductItem = ({ item, onItemClick }: { item: MenuItem; onItemClick: () => void }) => {
  const ProductContent = () => (
    <>
      <div className="relative h-32 w-32">
        <Image
          src={item.image ?? '/images/placeholder.png'}
          alt={item.name || 'Product image'}
          fill
          sizes="128px"
          className="rounded-lg transition-transform duration-200 hover:scale-105 mix-blend-multiply object-contain"
          priority
          loading="eager"
        />
      </div>
      <div className="mt-1 w-full items-center text-center">
        <Typography weight="bd" size="sm" className="font-medium text-[#151515]">
          {item.name}
        </Typography>
        {item.specs && (
          <Typography weight="bk" size="xs" className="text-[#151515]">
            {item.specs}
          </Typography>
        )}
      </div>
    </>
  );

  return (
    <div className="flex w-36 flex-col items-center rounded-lg bg-[#F6F6F6] px-1 pt-2 pb-3 transition-shadow duration-200 hover:shadow-md">
      {item.link ? (
        <Link
          href={item.link}
          className="flex h-full w-full flex-col items-center"
          onClick={onItemClick}
        >
          <ProductContent />
        </Link>
      ) : (
        <ProductContent />
      )}
    </div>
  );
};

// Multi Column Content Component (for scenarios)
const MultiColumnContent = ({
  items,
  onItemClick
}: {
  items: MenuItem[];
  onItemClick: () => void;
}) => {
  if (!items || items.length === 0) return null;

  const ScenarioContent = ({ item }: { item: MenuItem }) => (
    <>
      <div className="relative h-[121px] w-full">
        <Image
          src={item.image ?? '/images/placeholder.png'}
          alt={item.name || 'Scenario image'}
          fill
          sizes="192px"
          className="h-full object-cover"
          quality={80}
          priority
          loading="eager"
        />
      </div>
      <div className="w-full p-2">
        <h4 className="font-medium">{item.name}</h4>
      </div>
    </>
  );

  return (
    <div className="flex flex-row flex-wrap gap-6">
      {items.map((item) => (
        <div
          key={item.id}
          className="bg-grey-50 flex w-48 flex-col items-center overflow-hidden rounded-lg transition-shadow duration-200 hover:shadow-md"
        >
          {item.link ? (
            <Link href={item.link} className="inline-flex w-full flex-col" onClick={onItemClick}>
              <ScenarioContent item={item} />
            </Link>
          ) : (
            <ScenarioContent item={item} />
          )}
        </div>
      ))}
    </div>
  );
};

// List Content Component (for support/about)
const ListContent = ({ items, onItemClick }: { items: MenuItem[]; onItemClick: () => void }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-5">
      {items.map((item) => (
        <div
          key={item.id || item.name}
          className="transition-colors duration-200 hover:text-[#263AEA]"
        >
          {item.link ? (
            <Link href={item.link} className="inline-block" onClick={onItemClick}>
              <Typography weight="bk" size="sm">
                {item.name}
              </Typography>
            </Link>
          ) : (
            <Typography weight="bk" size="sm">
              {item.name}
            </Typography>
          )}
        </div>
      ))}
    </div>
  );
};

// Category Sidebar Component
const CategorySidebar = ({
  state,
  dispatch,
  activeMenu
}: {
  state: MenuState;
  dispatch: React.Dispatch<MenuAction>;
  activeMenu: Category | undefined;
}) => {
  const {
    activeCategory,
    activeSubCategory,
    activeNestedSubCategory,
    activeSort,
    showProductSort
  } = state;

  if (!activeMenu?.subcategories) return null;

  // Helper function for rendering sidebar buttons
  const renderSidebarButton = (
    key: string,
    name: string,
    isActive: boolean,
    onClick: () => void,
    showChevron = true
  ) => (
    <button
      key={key}
      tabIndex={0}
      className={`flex w-full cursor-pointer items-center justify-between p-3 text-left transition-colors duration-200 hover:bg-gray-50 ${
        isActive ? 'bg-[#EDEEF0]' : ''
      }`}
      onMouseEnter={onClick}
      role="menuitem"
    >
      <Typography weight={isActive ? 'md' : 'bk'} size="sm" className="text-black">
        {name}
      </Typography>
      {showChevron && <ChevronRight className="h-4 w-4" />}
    </button>
  );

  // Helper function for rendering nested sidebar buttons
  const renderNestedButton = (
    key: string,
    name: string,
    isActive: boolean,
    onClick: () => void
  ) => (
    <button
      key={key}
      tabIndex={0}
      className={`w-full cursor-pointer p-2 text-left transition-colors duration-200 hover:bg-gray-50 ${
        isActive ? 'bg-[#EDEEF0]' : ''
      }`}
      onMouseEnter={onClick}
    >
      <Typography weight={isActive ? 'md' : 'bk'} size="xs" className="text-black">
        {name}
      </Typography>
    </button>
  );

  // Special case for EV Charging sidebar
  if (activeCategory === 'evCharging') {
    return (
      <div className="col-span-3 h-full border-r">
        <div className="flex flex-col space-y-2">
          {/* Main subcategories */}
          {Object.entries(activeMenu.subcategories || {}).map(([key, subcategory]) =>
            renderSidebarButton(
              key,
              (subcategory as SubCategory).name,
              activeSubCategory === key,
              () => dispatch({ type: 'SET_SUBCATEGORY', payload: key })
            )
          )}

          {/* Nested subcategories for accessories */}
          {activeSubCategory === 'accessories' &&
            activeMenu.subcategories?.accessories?.type === 'nested' &&
            activeMenu.subcategories.accessories.subcategories && (
              <div className="ml-4 flex flex-col">
                {Object.entries(activeMenu.subcategories.accessories.subcategories).map(
                  ([key, nestedSubcat]) =>
                    renderNestedButton(
                      key,
                      (nestedSubcat as SubCategory).name,
                      activeNestedSubCategory === key,
                      () => dispatch({ type: 'SET_NESTED_SUBCATEGORY', payload: key })
                    )
                )}
              </div>
            )}
        </div>
      </div>
    );
  }

  // Sorting options sidebar for categories with products/scenarios
  const hasSortingOptions = [
    'portablePowerStations',
    'evCharging',
    'commercialAndHomeStorage'
  ].includes(activeCategory);
  const isInfoCategory = activeCategory === 'support' || activeCategory === 'about';

  if (hasSortingOptions) {
    return (
      <div className="col-span-3 h-full border-r">
        <div className="flex flex-col space-y-2">
          {/* Sort by Products button */}
          <button
            className="group flex w-full cursor-pointer items-center justify-between p-3 text-left transition-colors duration-200 hover:bg-gray-50"
            onMouseEnter={() => dispatch({ type: 'SET_SORT_TYPE', payload: 'products' })}
          >
            <Typography
              weight={activeSort === 'products' ? 'bd' : 'bk'}
              size="sm"
              className="text-[#212529]"
            >
              Sort by Products
            </Typography>
            {activeSort === 'products' ? (
              <ChevronDown className="h-4 w-4 text-[#495057]" />
            ) : (
              <ChevronRight className="h-4 w-4 text-[#495057]" />
            )}
          </button>

          {/* Product subcategories */}
          {showProductSort && activeMenu.subcategories.byProduct?.subcategories && (
            <div className="ml-4 flex flex-col">
              {Object.entries(activeMenu.subcategories.byProduct.subcategories).map(
                ([key, subcategory]) =>
                  renderNestedButton(
                    key,
                    (subcategory as SubCategory).name,
                    activeSubCategory === 'byProduct' && activeNestedSubCategory === key,
                    () => {
                      dispatch({ type: 'SET_SUBCATEGORY', payload: 'byProduct' });
                      dispatch({ type: 'SET_NESTED_SUBCATEGORY', payload: key });
                    }
                  )
              )}
            </div>
          )}

          {/* Sort by Scenarios button */}
          {/* <button
            className="flex w-full cursor-pointer items-center justify-between p-3 text-left transition-colors duration-200 hover:bg-gray-50"
            onMouseEnter={() => dispatch({ type: 'SET_SORT_TYPE', payload: 'scenarios' })}
          >
            <Typography
              weight={activeSort === 'scenarios' ? 'bd' : 'bk'}
              size="sm"
              className="text-[#212529]"
            >
              Sort by Scenarios
            </Typography>
            {activeSort === 'scenarios' ? (
              <ChevronDown className="h-4 w-4 text-[#495057]" />
            ) : (
              <ChevronRight className="h-4 w-4 text-[#495057]" />
            )}
          </button> */}
        </div>
      </div>
    );
  }

  // Standard sidebar (for support, about, or categories without sorting)
  return (
    <div className="col-span-3 h-full border-r">
      {Object.entries(activeMenu.subcategories || {}).map(([key, subcategory]) =>
        renderSidebarButton(
          key,
          (subcategory as SubCategory).name,
          activeSubCategory === key,
          () => dispatch({ type: 'SET_SUBCATEGORY', payload: key }),
          !isInfoCategory
        )
      )}
    </div>
  );
};

// Content Components
const renderProductSection = (
  title: string,
  items: MenuItem[] = [],
  viewAllLink: string = '',
  onItemClick: () => void,
  currentCategory?: string
) => {
  // Categories that should hide the "View All" link
  const hideViewAllCategories = [
    'portablePowerStations',
    'evCharging', 
    'homeStorage',
    'commercial'
  ];
  
  const shouldHideViewAll = currentCategory && hideViewAllCategories.includes(currentCategory);

  return (
    <div className="w-full">
      <div className="mb-4 flex flex-row items-center justify-between">
        <Typography weight="lt" size="sm" className="text-black">
          {title}
        </Typography>
        {!shouldHideViewAll && viewAllLink && viewAllLink.trim() !== '' && (
          <Link href={viewAllLink} onClick={onItemClick}>
            <div className="group flex cursor-pointer items-center gap-2">
              <Typography
                weight="bk"
                size="sm"
                className="text-[#495057] transition-colors duration-200 group-hover:text-[#263AEA]"
              >
                View All
              </Typography>
              <ChevronRight className="h-4 w-4 text-[#495057] transition-colors duration-200 group-hover:text-[#263AEA]" />
            </div>
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        {items.map((item) => (
          <ProductItem key={item.id} item={item} onItemClick={onItemClick} />
        ))}
      </div>
    </div>
  );
};

// Helper function to render content based on subcategory type
const renderContentForType = (subcategory: SubCategory, onItemClick: () => void, currentCategory?: string) => {
  switch (subcategory.type) {
    case 'list':
      return <ListContent items={subcategory.items || []} onItemClick={onItemClick} />;
    case 'multi-column':
      return <MultiColumnContent items={subcategory.items || []} onItemClick={onItemClick} />;
    case 'products':
    default:
      return subcategory.items?.length
        ? renderProductSection(
            subcategory.name,
            subcategory.items,
            subcategory.viewAllLink,
            onItemClick,
            currentCategory
          )
        : null;
  }
};

// Product Category Content Component - Refactored to use helper functions
const ProductCategoryContent = ({
  state,
  onItemClick
}: {
  state: MenuState;
  onItemClick: () => void;
}) => {
  const { activeCategory, activeSubCategory, activeNestedSubCategory } = state;
  const selectedCategory = menuData[activeCategory];

  if (!selectedCategory || !selectedCategory.subcategories) return null;

  const subcategory = selectedCategory.subcategories[activeSubCategory];
  if (!subcategory) return null;

  // Handle regular subcategory
  if (subcategory.type !== 'nested' || !subcategory.subcategories) {
    return renderContentForType(subcategory, onItemClick, activeCategory);
  }

  // Handle nested subcategory
  const subcategories = subcategory.subcategories;

  // Case 1: We have an active nested subcategory that exists
  if (
    activeNestedSubCategory &&
    Object.prototype.hasOwnProperty.call(subcategories, activeNestedSubCategory)
  ) {
    const nestedSubcategory = subcategories[activeNestedSubCategory as keyof typeof subcategories];
    if (nestedSubcategory) {
      return renderContentForType(nestedSubcategory, onItemClick, activeCategory);
    }
  }

  // Case 2: No active nested subcategory, show first one
  const subcategoryKeys = Object.keys(subcategories);
  if (subcategoryKeys.length > 0) {
    const firstKey = subcategoryKeys[0];
    const firstSubcategory = subcategories[firstKey as keyof typeof subcategories];
    if (firstSubcategory) {
      return renderContentForType(firstSubcategory, onItemClick, activeCategory);
    }
  }

  return null;
};

// Main Content Component
const MenuContent = ({ state, onItemClick }: { state: MenuState; onItemClick: () => void }) => {
  const { activeCategory, activeSubCategory, activeSort } = state;

  const activeMenu = menuData[activeCategory];
  if (!activeMenu) return null;

  // List type categories (support/about)
  if (activeMenu.type === 'list' && activeMenu.items) {
    return <ListContent items={activeMenu.items} onItemClick={onItemClick} />;
  }

  // Scenarios view
  if (activeSort === 'scenarios' && activeMenu.subcategories?.byScenarios) {
    return (
      <MultiColumnContent
        items={activeMenu.subcategories.byScenarios.items || []}
        onItemClick={onItemClick}
      />
    );
  }

  // Get current subcategory
  const currentSubcategory = activeMenu.subcategories?.[activeSubCategory];
  if (!currentSubcategory) return null;

  // Determine content type and render accordingly
  switch (currentSubcategory.type) {
    case 'list':
      return <ListContent items={currentSubcategory.items || []} onItemClick={onItemClick} />;
    case 'multi-column':
      return (
        <MultiColumnContent items={currentSubcategory.items || []} onItemClick={onItemClick} />
      );
    case 'products':
    case 'nested':
    default:
      return <ProductCategoryContent state={state} onItemClick={onItemClick} />;
  }
};

// Main MegaMenu Component
const MegaMenu = () => {
  const [state, dispatch] = useReducer(menuReducer, initialState);
  const { searchOpen, isMenuOpen, activeCategory } = state;

  const activeMenu = menuData[activeCategory];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        dispatch({ type: 'SET_SEARCH', payload: false });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handler for clicking an item - memoized to prevent unnecessary re-renders
  const handleItemClick = useCallback(() => dispatch({ type: 'CLOSE_MENU' }), []);

  return (
    <div className="hidden w-full lg:flex">
      {!searchOpen ? (
        <div className="mx-auto w-full max-w-7xl">
          <div className="flex h-16 items-center justify-between">
            {/* Navigation items */}
            <div className="flex items-center justify-center space-x-[26px] px-12">
              {Object.keys(menuData).map((key) => (
                <button
                  key={key}
                  className="group relative inline-flex cursor-pointer items-center gap-[6px] px-1 py-2 hover:text-[#212529]"
                  onMouseEnter={() => dispatch({ type: 'SET_CATEGORY', payload: key })}
                  aria-label={menuData[key]?.name}
                >
                  <Typography
                    weight="md"
                    size="sm"
                    className={
                      activeCategory === key
                        ? 'text-[#212529]'
                        : 'text-[#495057] group-hover:text-[#212529]'
                    }
                  >
                    {menuData[key]?.name}
                  </Typography>
                  <ChevronDown
                    className={`h-4 w-4 ${activeCategory === key ? 'text-[#212529]' : 'text-[#495057] group-hover:text-[#212529]'}`}
                  />

                  {/* Gradient indicator */}
                  <div
                    className={`absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-[#14BBD3] to-[#263AEA] transition-all duration-300 ${
                      activeCategory === key ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  ></div>
                </button>
              ))}
            </div>

            {/* Icons */}
            <div className="mr-4 flex items-center gap-6">
              {/* <BlockElement variant="ghost" size="icon">
                <SearchToggle
                  searchOpen={searchOpen}
                  setSearchOpen={() => dispatch({ type: 'TOGGLE_SEARCH' })}
                />
              </BlockElement>
              <Button variant={'ghost'} size={'icon'}>
                <User className="h-5 w-5 text-[#495057] transition-colors duration-200 hover:text-[#212529]" />
              </Button> */}
              <BlockElement variant="ghost" size="icon">
                <CartModal />
              </BlockElement>
            </div>
          </div>
        </div>
      ) : (
        <SearchToggle
          searchOpen={searchOpen}
          setSearchOpen={() => dispatch({ type: 'TOGGLE_SEARCH' })}
        />
      )}

      {/* Animated Mega Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.menu
            key="megamenu"
            className="fixed top-16 left-0 z-50 w-full bg-white shadow-lg"
            onMouseLeave={() => dispatch({ type: 'CLOSE_MENU' })}
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
          >
            <div className="mx-auto max-w-7xl p-6">
              <div className="grid grid-cols-12 gap-6">
                {/* Sidebar */}
                <CategorySidebar state={state} dispatch={dispatch} activeMenu={activeMenu} />

                {/* Content Area */}
                <div className="col-span-9 p-4">
                  <MenuContent state={state} onItemClick={handleItemClick} />
                </div>
              </div>
            </div>
          </motion.menu>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MegaMenu;
